import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { env } from '../config/env.js'
import ProfileBar from '../components/review/ProfileBar.jsx'
import EditableField from '../components/review/EditableField.jsx'
import { timeAgo } from '../lib/timeAgo.js'

const KIND_LABELS = { decouverte: 'Découverte', bilan_annuel: 'Bilan annuel', suivi: 'Suivi', recommandation: 'Recommandation' }

export default function ClientDetailPage() {
  const { id } = useParams()
  const { token, onUnauthorized } = useAuth()
  const [client, setClient] = useState(null)
  const [sessions, setSessions] = useState([])
  const [err, setErr] = useState(null)

  useEffect(() => {
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    Promise.all([api.getClient(id), api.listClientSessions(id)])
      .then(([c, s]) => { setClient(c); setSessions(s || []) })
      .catch((e) => setErr(e.message))
  }, [id, token, onUnauthorized])

  const patch = useCallback(async (partial) => {
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    const updated = await api.patchClient(id, partial)
    setClient(updated)
  }, [id, token, onUnauthorized])

  if (err) return <div className="p-12 text-center text-terra-600">{err}</div>
  if (!client) return <div className="p-12 text-center text-muted">Chargement…</div>

  const profile = {
    civilStatus: client.CivilStatus || client.civil_status || 'marie',
    hasChildren: client.HasChildren ?? client.has_children ?? false,
    employment: client.Employment || client.employment || 'salarie',
    ownership: client.Ownership || client.ownership || 'proprietaire',
  }

  return (
    <div className="space-y-8 px-7 py-8 md:px-12 lg:px-20">
      <header>
        <div className="text-[12px] text-muted">Clients › {client.Name || client.name}</div>
        <h1 className="mt-1 text-[22px] font-semibold tracking-[-0.02em]">{client.Name || client.name}</h1>
        <div className="mt-1 text-[13px] text-muted">{client.Email || client.email} · {client.Phone || client.phone || '—'}</div>
      </header>

      <section>
        <h2 className="mb-3 text-[14px] font-medium">Profil</h2>
        <ProfileBar
          profile={profile}
          onChange={(next) => patch({
            civil_status: next.civilStatus,
            has_children: next.hasChildren,
            employment: next.employment,
            ownership: next.ownership,
          })}
        />
      </section>

      <section>
        <h2 className="mb-3 text-[14px] font-medium">Notes</h2>
        <EditableField
          label=""
          value={client.Notes || client.notes || ''}
          onCommit={(v) => patch({ notes: v })}
        />
      </section>

      <section>
        <h2 className="mb-3 text-[14px] font-medium">Historique des séances</h2>
        {sessions.length === 0 && <p className="text-[12.5px] text-muted">Aucune séance pour ce client.</p>}
        {sessions.length > 0 && (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li key={s.ID || s.id}>
                <Link to={`/sessions/${s.ID || s.id}`} className="block rounded-xl border border-line bg-white px-5 py-3 hover:border-terra-500">
                  <div className="text-[13.5px] font-medium">{KIND_LABELS[s.Kind || s.kind] || (s.Kind || s.kind)}</div>
                  <div className="text-[11.5px] text-muted">{timeAgo(s.StartedAt || s.started_at)} · {s.State || s.state}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
